import { Unit, } from '@thoughtspot/ts-chart-sdk';
import { isAttribute, isGrowth, isMeasure, isMeasureName, isMeasureValues, } from '@thoughtspot/visualization-column-util';
import { clone } from 'lodash';
import { CategoryType, NegativeValueFormat, } from '../types/prism';
const PROTO_TO_UNITS = {
    1: Unit.None,
    2: Unit.Thousands,
    3: Unit.Million,
    4: Unit.Billion,
    5: Unit.Trillion,
    6: Unit.Auto,
};
const PROTO_TO_CATEGORY = {
    1: CategoryType.Number,
    2: CategoryType.Percentage,
    3: CategoryType.Currency,
    4: CategoryType.Custom,
};
const PROTO_TO_NEGATIVE_VALUE_FORMAT = {
    1: NegativeValueFormat.PrefixDash,
    2: NegativeValueFormat.SuffixDash,
    3: NegativeValueFormat.BracesNodash,
};
export const defaultFormatConfig = (column) => {
    let numberFormatConfig = null;
    let percentageFormatConfig = null;
    let categoryType = CategoryType.Number;
    if (isAttribute(column) ||
        isMeasureName(column) ||
        isMeasureValues(column)) {
        return null;
    }
    if (isGrowth(column)) {
        categoryType = CategoryType.Percentage;
        percentageFormatConfig = {
            __typename: 'PercentageFormatConfig',
            decimals: 2,
            removeTrailingZeroes: false,
        };
    }
    else if (isMeasure(column)) {
        const customFormat = column.format || column.formatPattern;
        if (customFormat) {
            return null;
        }
        categoryType = CategoryType.Number;
        numberFormatConfig = {
            __typename: 'NumberFormatConfig',
            decimals: 0,
            negativeValueFormat: NegativeValueFormat.PrefixDash,
            removeTrailingZeroes: false,
            toSeparateThousands: true,
            unit: Unit.Auto,
        };
    }
    const formatConfig = {
        __typename: 'FormatConfig',
        category: categoryType,
        isCategoryEditable: true,
        numberFormatConfig,
        percentageFormatConfig,
    };
    return formatConfig;
};
const applyFormatConfigDefaults = (formatConfig) => {
    const config = {
        ...formatConfig,
        numberFormatConfig: clone(formatConfig.numberFormatConfig),
        percentageFormatConfig: clone(formatConfig.percentageFormatConfig),
        currencyFormatConfig: clone(formatConfig.currencyFormatConfig),
        customFormatConfig: clone(formatConfig.customFormatConfig),
    };
    const numberConfig = config.numberFormatConfig;
    const percentageConfig = config.percentageFormatConfig;
    const currencyConfig = config.currencyFormatConfig;
    const customConfig = config.customFormatConfig;
    config.category = config.category ?? CategoryType.Number;
    config.isCategoryEditable = config.isCategoryEditable ?? true;
    if (numberConfig) {
        numberConfig.decimals = numberConfig.decimals ?? 0;
        numberConfig.negativeValueFormat =
            numberConfig.negativeValueFormat ?? NegativeValueFormat.PrefixDash;
        numberConfig.removeTrailingZeroes =
            numberConfig.removeTrailingZeroes ?? false;
        numberConfig.toSeparateThousands =
            numberConfig.toSeparateThousands ?? true;
        numberConfig.unit = numberConfig.unit ?? Unit.Auto;
    }
    if (percentageConfig) {
        percentageConfig.decimals = percentageConfig.decimals ?? 2;
        percentageConfig.removeTrailingZeroes =
            percentageConfig.removeTrailingZeroes ?? false;
    }
    if (currencyConfig) {
        currencyConfig.decimals = currencyConfig.decimals ?? 2;
        currencyConfig.removeTrailingZeroes =
            currencyConfig.removeTrailingZeroes ?? false;
        currencyConfig.toSeparateThousands =
            currencyConfig.toSeparateThousands ?? true;
        currencyConfig.unit = currencyConfig.unit ?? Unit.Million;
    }
    if (customConfig) {
        customConfig.format = customConfig.format ?? null;
    }
    return {
        ...config,
        numberFormatConfig: numberConfig,
        percentageFormatConfig: percentageConfig,
        currencyFormatConfig: currencyConfig,
        customFormatConfig: customConfig,
    };
};
export const getNumberFormatting = (column) => {
    const rawConfig = column?.columnProps?.columnProperties?.format ?? null;
    if (!rawConfig)
        return defaultFormatConfig(column);
    const formatConfig = applyFormatConfigDefaults(rawConfig);
    if (typeof formatConfig.category === 'number') {
        formatConfig.category = PROTO_TO_CATEGORY[formatConfig.category];
    }
    if (formatConfig.numberFormatConfig &&
        typeof formatConfig.numberFormatConfig.unit === 'number') {
        formatConfig.numberFormatConfig.unit =
            PROTO_TO_UNITS[formatConfig.numberFormatConfig.unit];
    }
    if (formatConfig.numberFormatConfig &&
        typeof formatConfig.numberFormatConfig.negativeValueFormat === 'number') {
        formatConfig.numberFormatConfig.negativeValueFormat =
            PROTO_TO_NEGATIVE_VALUE_FORMAT[formatConfig.numberFormatConfig.negativeValueFormat];
    }
    if (formatConfig.currencyFormatConfig &&
        typeof formatConfig.currencyFormatConfig.unit === 'number') {
        formatConfig.currencyFormatConfig.unit =
            PROTO_TO_UNITS[formatConfig.currencyFormatConfig.unit];
    }
    return formatConfig;
};
//# sourceMappingURL=custom-chart-formatting.utils.js.map